import { INVALID_STRING_VALUES, STANDARD_EVENT_PREFIX } from '../constants';
import { errorTypes } from './error-types';

// Options guide: https://repro.esa.io/posts/21153
interface ValidationOptions {
  type?: string; // Set validation type 'string', 'double', 'int' or 'date'
  allowNull?: boolean; // Allows all value types to be null
  allowNullString?: boolean; // Allows only strings to be null
  denyEmptyString?: boolean; // Denies strings that are empty ''
  denyStandardPrefix?: boolean; // Denies strings starting with '___repro___'
  maxStringLength?: number; // Set the maximum length of string value
}

const validateStringValue = (value: string, options: ValidationOptions = {}): [boolean, string] | [boolean, null] => {
  if (!options.maxStringLength) {
    return [false, 'No max string length'];
  }

  // Value Validation
  if (typeof value !== 'string') {
    return [false, errorTypes.NOT_STRING];
  }
  if (value === '' && !!options.denyEmptyString) {
    return [false, errorTypes.EMPTY_STRING];
  }
  if (INVALID_STRING_VALUES.includes(value)) {
    return [false, errorTypes.INVALID_STRING];
  }
  if ([...value].length > options.maxStringLength) {
    return [false, errorTypes.EXCEED_LENGTH];
  }
  if (value.startsWith(STANDARD_EVENT_PREFIX) && !!options.denyStandardPrefix) {
    return [false, errorTypes.NO_STANDARD_PREFIX];
  }
  return [true, null];
};

const validateDoubleValue = (value: number): [boolean, string | null] => {
  if (typeof value !== 'number') {
    return [false, errorTypes.NOT_DOUBLE_NUMBER];
  }
  if (isNaN(value) || !isFinite(value)) {
    return [false, errorTypes.INVALID_DOUBLE_NUMBER];
  }
  return [true, null];
};

const validateIntValue = (value: number): [boolean, string | null] => {
  if (typeof value !== 'number') {
    return [false, errorTypes.NOT_INT_NUMBER];
  }
  if (!Number.isInteger(value)) {
    return [false, errorTypes.INVALID_INT_NUMBER];
  }
  if (isNaN(value) || !isFinite(value)) {
    return [false, errorTypes.INVALID_INT_NUMBER];
  }
  return [true, null];
};

const validateDateValue = (value: Date): [boolean, string | null] => {
  if (!(value instanceof Date)) {
    return [false, errorTypes.NOT_DATE];
  }
  if (isNaN(value.getTime())) {
    return [false, errorTypes.INVALID_DATE];
  }
  return [true, null];
};

const validateFunctionValue = (value: Function): [boolean, string] | [boolean, null] => {
  if (typeof value !== 'function') {
    return [false, errorTypes.NOT_FUNCTION];
  }
  return [true, null];
};
const validateBooleanValue = (value: boolean): [boolean, string | null] => {
  if (typeof value !== 'boolean') {
    return [false, errorTypes.NOT_BOOLEAN];
  }
  return [true, null];
};

export const validateValue = (value: any, options: ValidationOptions = {}): [boolean, string | null] => {
  let valType = options.type;
  if (!options.type) {
    if (value instanceof Date) valType = 'date';
    else valType = typeof value;
  }

  if (value === undefined) {
    return [false, errorTypes.UNDEFINED];
  }
  if (value === null) {
    if (options.allowNull) return [true, null];
    if (valType === 'string' && options.allowNullString) return [true, null];
    return [false, errorTypes.NULL];
  }

  switch (valType) {
    case 'string':
      return validateStringValue(value, options);
    case 'int':
      return validateIntValue(value);
    case 'double':
    case 'number':
      return validateDoubleValue(value);
    case 'date':
      return validateDateValue(value);
    case 'function':
      return validateFunctionValue(value);
    case 'boolean':
      return validateBooleanValue(value);
    default:
      return [false, errorTypes.INVALID_TYPE];
  }
};
